import type { PipelineId } from '../types/ids.ts'

/**
 * What a flow IS, as a value: one graph of nodes, stored as an opaque JSON blob
 * on `flows.graph` (ADR-0006 §5, ADR-0007 §1).
 *
 * The whole of this folder is pure — no clock, no I/O, no runtime dependency —
 * because every one of these questions must answer the same way at save time in
 * the engine, at draw time in the console, and at drain time under the pump. A
 * validator that could read the database would answer differently in each of
 * those places, and a graph that saved would not be a graph that runs.
 */

/* ------------------------------------------------------------- node types */

/**
 * The closed v1 registry (ADR-0007 §6). **Four**, not the plan's three: Q12 was
 * ruled on 2026-08-27 — when an order moves to «cancelled» the flow must close
 * or archive the Delivery card it created, so create-only does not close the
 * loop the worked example opens.
 */
export type NodeType =
  | 'trigger.status_entered'
  | 'condition'
  | 'create_lead'
  | 'update_lead'
  | 'send_email'

/**
 * What a node does to the run, as opposed to what it does to a lead.
 *
 * It is declared rather than derived from the type's spelling, because two of
 * the three answers carry rules the validator enforces: exactly one `trigger`
 * per graph and it is the entry, and at most one `action` per graph and nothing
 * follows it (ADR-0007 §5).
 */
export type NodeRole = 'trigger' | 'condition' | 'action'

/**
 * The runtime list, closed at COMPILE time in both directions — the
 * `EVENT_TYPE_COVERAGE` trick (`../events/index.ts`). A member of
 * {@link NodeType} with no key here is an error, and a key naming no member is
 * an error, so there is no list to drift and no test that has to remember to
 * pin it.
 */
const NODE_TYPE_COVERAGE: Record<NodeType, NodeRole> = {
  'trigger.status_entered': 'trigger',
  condition: 'condition',
  create_lead: 'action',
  update_lead: 'action',
  /**
   * **Five, not four** (#377). An action, and therefore subject to the same two
   * rules: one action per graph, and nothing after it.
   *
   * That the mail cannot share a graph with a `create_lead` is a consequence
   * rather than an oversight. Two flows on one trigger both fire, so «create
   * the Delivery card» and «tell the customer» stay two flows — which is also
   * the only shape in which one of them can be disabled without the other.
   */
  send_email: 'action',
}

export const NODE_TYPES: readonly NodeType[] = Object.keys(NODE_TYPE_COVERAGE) as NodeType[]

/** Total over the closed union: every node type has exactly one role. */
export const nodeRole = (type: NodeType): NodeRole => NODE_TYPE_COVERAGE[type]

/** Narrow a stored `type` string into the registry this build knows. */
export function isKnownNodeType(type: string): type is NodeType {
  return Object.prototype.hasOwnProperty.call(NODE_TYPE_COVERAGE, type)
}

/* ----------------------------------------------------------- the graph */

/** An integer grid cell. Clamping and snapping are the canvas's job (D9). */
export interface NodePosition {
  x: number
  y: number
}

/** Which of a node's two exits an edge leaves by. Every node has exactly two. */
export type NodeBranch = 'resolve' | 'reject'
export const NODE_BRANCHES: readonly NodeBranch[] = ['resolve', 'reject'] as const

/**
 * One node of the graph.
 *
 * `id` is minted by the console (a nanoid) and never swapped on save; `key` is
 * the human name a template and a run step cite. Both exist because a run has
 * to be joinable back to a graph that has since been edited: the console joins
 * by `id` and falls back to `key` for a node that has been deleted (ADR-0007
 * §6).
 *
 * `options` is `Record<string, unknown>` and not a per-type union, because this
 * value is read back out of a JSON blob written by an older console against an
 * older registry. Narrowing happens through the node's spec — `validate` at
 * save, `parse` at run — never by asserting the blob is what it claims.
 */
export interface FlowNode {
  id: string
  key: string
  type: NodeType
  options: Record<string, unknown>
  resolve: string | null
  reject: string | null
  position: NodePosition
}

/**
 * The stored graph.
 *
 * `retiredKeys` is what makes the run→graph fallback sound. A run step cites
 * `nodeId` **and** `key`, and the console falls back to the key when the node
 * is gone — which is only correct while a key names at most one node in the
 * flow's whole history. Deleting a node therefore retires its key here and
 * {@link FlowIssue} `retired_key` refuses to mint it again; a reused key would
 * make a historical step resolve to the replacement node and quietly misreport
 * what ran. Directus has no unique on `(flow, key)` at all — key uniqueness is
 * a Vue computed there, and at runtime a duplicate key silently overwrites the
 * earlier slot (study §1, `flows.ts:420`).
 *
 * It lives inside the blob rather than in a table so the validator stays a pure
 * function of one graph.
 */
export interface FlowGraph {
  nodes: FlowNode[]
  retiredKeys?: readonly string[]
}

/* ----------------------------------------------------------------- issues */

/**
 * Why one option of one node is unacceptable, as a node's own spec sees it.
 *
 * The codes are closed because the console renders each one as a translated
 * sentence; a free-text `message` would put an English string from `core` into
 * a Ukrainian form. `message` is the developer-facing detail and is never what
 * a person reads.
 */
export type NodeIssueCode =
  | 'required'
  | 'unknown_option'
  | 'not_a_string'
  | 'not_a_number'
  | 'not_a_boolean'
  | 'not_an_array'
  | 'not_an_object'
  | 'invalid_enum'
  | 'empty'
  | 'not_templatable'
  | 'nothing_to_do'
  | 'unknown_operator'
  | 'invalid_rule'

export interface NodeIssue {
  code: NodeIssueCode
  /** The option this is about; absent when the issue is about the node itself. */
  field?: string
  message: string
}

/**
 * Why a graph is unacceptable.
 *
 * Every {@link NodeIssueCode} is also a graph issue code: a node's own verdict
 * lifts verbatim and gains the `nodeId`/`key` that locate it, rather than being
 * rewrapped into a second vocabulary the console would have to translate twice.
 */
export type FlowIssueCode =
  | NodeIssueCode
  | 'not_a_graph'
  | 'no_trigger'
  | 'multiple_triggers'
  | 'trigger_is_targeted'
  | 'trigger_has_reject'
  | 'multiple_actions'
  | 'action_has_successor'
  | 'unknown_node_type'
  | 'duplicate_node_id'
  | 'duplicate_key'
  | 'reserved_key'
  | 'invalid_key'
  | 'retired_key'
  | 'invalid_node'
  | 'invalid_position'
  | 'edge_target_missing'
  | 'edge_self'
  | 'multiple_parents'
  | 'cycle'
  | 'template_unknown_root'
  | 'template_unknown_path'
  | 'template_not_upstream'
  | 'template_unnamed_pipeline'

export interface FlowIssue {
  code: FlowIssueCode
  /** The node this is about; absent for a whole-graph verdict. */
  nodeId?: string
  key?: string
  field?: string
  /** The `{{ }}` path or rule path the issue is about, when it is one. */
  path?: string
  message: string
}

/* ------------------------------------------------------------ the chain */

/**
 * The data chain a template or a rule reads (ADR-0007 §7). `$last` is the
 * previous step's return; a node's own key reaches any earlier node's.
 *
 * No `$accountability` — `actor` still cannot name a person, and a flow writes
 * `automation:<flowId>`. No `$env`: secrets never enter a chain, which is
 * cheaper than Directus's allow-list-then-redact and cannot be got wrong.
 */
export type ChainRoot = '$event' | '$lead' | '$sale' | '$last'
export const CHAIN_ROOTS: readonly ChainRoot[] = ['$event', '$lead', '$sale', '$last'] as const

/**
 * The source card's cycle slice, pre-filled for every pipeline the graph names
 * (ADR-0007 §7). The pre-fill is not a convenience: it is what stops "missing
 * field ⇒ false" from silently disarming the revival rule
 * `{ "$sale": { "openCardsIn": { "<target>": { "_eq": 0 } } } }`, which is the
 * one rule the worked example depends on.
 */
export interface SaleSlice {
  ludusLeadId: string | null
  openCardsIn: Record<PipelineId, number>
}

/** The chain as a node sees it: the four roots plus every upstream node's key. */
export type DataChain = Record<string, unknown>
